import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Pagination,
  Tooltip,
  Typography,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  useTheme
} from '@mui/material';
import { Visibility, Delete, Restore, Search, Clear, WarningAmber } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import { 
  getTrashedContacts, 
  restoreContact, 
  deleteContactPermanently, 
  getContact
} from 'src/ApiServices';
import ContactModal from 'src/sections/dashboard-sections/ContactModal';

const ContactTrash = ({ searchQuery: externalSearchQuery, onSearchChange }) => {
  // State Management
  const theme = useTheme();
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [trashData, setTrashData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedContact, setSelectedContact] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });

  // Use external search query if provided, otherwise use internal state
  const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : internalSearchQuery;
  const setSearchQuery = onSearchChange || setInternalSearchQuery;

  // Data Transformation Functions
  const transformTrashData = (apiData) => {
    return apiData.map((contact) => ({
      id: contact.id || '-',
      name: contact.name || '-',
      email: contact.email || '-',
      contact_no: contact.contact_no || '-',
      message: contact.message || '-',
      response: contact.response || null,
      created_at: contact.created_at || '-',
      deleted_at: contact.soft_deleted || '-',
      status: contact.status || 'soft_deleted',
      property_details: contact.property_details || null
    }));
  };

  // API Calls
  const fetchTrashData = async (currentPage = 1, search = '') => {
    try {
      setLoading(true);
      const response = await getContact({ 
        page: currentPage, 
        search: search ,
        status: 7
      });
      const transformedData = transformTrashData(response.data || []);
      setTrashData(transformedData);
      setPagination(response.pagination || {
        current_page: currentPage,
        per_page: 20,
        total: response.data?.length || 0,
        last_page: 1,
        has_more_pages: false
      });
    } catch (error) {
      console.error('Error fetching trashed contacts:', error);
      setTrashData([]);
      toast.error('Failed to load trashed contacts');
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    fetchTrashData(page, searchQuery);
  }, [page, searchQuery]);

  // Event Handlers
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleViewClick = (contact) => {
    setSelectedContact(contact);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedContact(null);
  };

  // Search handlers
  const handleSearchSubmit = () => {
    setPage(1);
    fetchTrashData(1, searchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(1);
    fetchTrashData(1, '');
  };

  // Trash Operations
  const handleRestore = async (contactId) => {
    try {
      setRestoreLoading(contactId);
      // Note: Make sure your restoreContact API expects the correct payload format
      await restoreContact({ id: contactId });
      toast.success('Contact restored successfully!');
      fetchTrashData(page, searchQuery);
    } catch (error) {
      console.error('Error restoring contact:', error);
      toast.error('Failed to restore contact');
    } finally {
      setRestoreLoading(null);
    }
  };

  const handleDeleteClick = (contact) => {
    setContactToDelete(contact);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;

    try {
      setDeleteLoading(contactToDelete.id);
      await deleteContactPermanently(contactToDelete.id);
      toast.success('Contact permanently deleted!');
      fetchTrashData(page, searchQuery);
      setDeleteModalOpen(false);
      setContactToDelete(null);
    } catch (error) {
      console.error('Error permanently deleting contact:', error);
      toast.error('Failed to delete contact permanently');
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setContactToDelete(null);
  };

  // Utility Functions
  const truncateMessage = (message, maxLength = 100) => {
    if (!message) return '-';
    if (message.length <= maxLength) return message;
    return `${message.substring(0, maxLength)}...`;
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '-') return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render property details component - Same as in ContactUs
  const renderPropertyDetails = (contact) => {
    if (!contact.property_details) {
      return (
        <Typography
          sx={{
            color: palette.text.secondary,
            fontSize: '13px'
          }}
        >
          No Listing Attached
        </Typography>
      );
    }

    const property = contact.property_details;

    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Property Image */}
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 1,
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: palette.grey[200]
          }}
        >
          <img
            src={property.main_image}
            alt={property.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: palette.grey[300]
            }}
          >
            <Typography variant="caption" color="textSecondary">
              No Image
            </Typography>
          </Box>
        </Box>

        {/* Property Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={property.title} arrow>
            <Typography
              sx={{
                fontFamily: 'poppinsMedium',
                fontSize: '14px',
                color: palette.text.primary,
                lineHeight: 1.2,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {truncateText(property.title, 25)}
            </Typography>
          </Tooltip>

          <Tooltip title={property.address} arrow>
            <Typography
              sx={{
                fontSize: '12px',
                color: palette.text.secondary,
                lineHeight: 1.2,
                mb: 0.5,
                mt: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {truncateText(property.address, 30)}
            </Typography>
          </Tooltip>

          <Typography
            sx={{
              fontSize: '11px',
              color: palette.text.disabled,
              lineHeight: 1.2,
              mt: 1.45
            }}
          >
            {property.city || 'N/A'} | {property.state || 'N/A'}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Table Configuration - Added Property Details column
  const trashTableHeaders = [
    'Property Details',
    'Name', 
    'Email', 
    'Contact No', 
    'Message', 
    'Deleted At', 
    'Actions'
  ];

  // Render Functions
  const renderSkeletonRows = (headers) =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        {headers.map((_, cellIndex) => (
          <TableCell key={cellIndex} sx={{ px: 2 }}>
            <Skeleton variant="text" height={25} />
          </TableCell>
        ))}
      </TableRow>
    ));

  const renderNoRecordsFound = (headers, message) => (
    <TableRow>
      <TableCell
        colSpan={headers.length}
        sx={{ textAlign: 'center', py: 8, borderBottom: 'none' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="h6"
            sx={{ color: palette.text.secondary, fontFamily: 'poppinsMedium', mb: 1 }}
          >
            {message.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: palette.grey[600],
              fontFamily: 'poppinsRegular',
              maxWidth: 400,
              textAlign: 'center'
            }}
          >
            {message.description}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      {/* Search Section for Trash - Only show when used independently */}
      {!onSearchChange && (
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={10}>
            <TextField
              variant="outlined"
              placeholder="Search trashed contacts..."
              size="medium"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearchSubmit}
                fullWidth
                sx={{
                  height: 56,
                  fontSize: '14px',
                  fontFamily: 'poppinsMedium'
                }}
                startIcon={<Search />}
              >
                Search
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      <Card>
        <TableContainer>
          <Table
            sx={{
              '& .MuiTableCell-head': {
                backgroundColor: palette.background.paper,
                color: palette.primary.tableHeadTitle,
                fontFamily: 'poppinsSemiBold',
                fontSize: '14px',
                borderBottom: `2px solid ${palette.divider}`,
                py: 2
              },
              '& .MuiTableCell-body': {
                color: palette.text.primary,
                fontFamily: 'poppinsRegular',
                fontSize: '14px',
                borderBottom: `1px solid ${palette.divider}`,
                py: 2
              }
            }}
          >
            <TableHead>
              <TableRow>
                {trashTableHeaders.map((header, index) => (
                  <TableCell key={index} sx={{ px: 2, whiteSpace: 'nowrap' }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? renderSkeletonRows(trashTableHeaders)
                : trashData.length > 0
                ? trashData.map((contact) => (
                    <TableRow
                      key={contact.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: palette.action.hover
                        }
                      }}
                    >
                      <TableCell sx={{ px: 2, minWidth: 200 }}>
                        {renderPropertyDetails(contact)}
                      </TableCell>
                      <TableCell sx={{ px: 2 }}>{contact.name}</TableCell>
                      <TableCell sx={{ px: 2 }}>{contact.email}</TableCell>
                      <TableCell sx={{ px: 2 }}>{contact.contact_no}</TableCell>
                      <TableCell sx={{ px: 2 }}>
                        <Tooltip title={contact.message || '-'} arrow>
                          <Typography
                            component="span"
                            sx={{
                              display: 'inline-block',
                              maxWidth: 200,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              cursor: 'pointer'
                            }}
                          >
                            {truncateMessage(contact.message)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ px: 2 }}>{formatDate(contact.deleted_at)}</TableCell>
                      <TableCell sx={{ px: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {/* View Button */}
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewClick(contact)}
                              sx={{
                                color: palette.primary.main,
                                '&:hover': {
                                  backgroundColor: palette.primary.lighter
                                }
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>

                          {/* Restore Button */}
                          <Tooltip title="Restore">
                            <IconButton
                              size="small"
                              onClick={() => handleRestore(contact.id)}
                              disabled={restoreLoading === contact.id || deleteLoading === contact.id}
                              sx={{
                                color: palette.success.main,
                                '&:hover': {
                                  backgroundColor: palette.success.lighter
                                }
                              }}
                            >
                              {restoreLoading === contact.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Restore />
                              )}
                            </IconButton>
                          </Tooltip>

                          {/* Permanent Delete Button */}
                          <Tooltip title="Delete Permanently">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(contact)}
                              disabled={restoreLoading === contact.id || deleteLoading === contact.id}
                              sx={{
                                color: palette.error.main,
                                '&:hover': {
                                  backgroundColor: palette.error.lighter
                                }
                              }}
                            >
                              {deleteLoading === contact.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Delete />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                : renderNoRecordsFound(trashTableHeaders, {
                    title: 'No Trashed Contacts Found',
                    description: searchQuery 
                      ? `No trashed contacts found for "${searchQuery}". Try a different search term.`
                      : 'No contact requests found in trash.'
                  })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pagination for Trash */}
      {!loading && trashData.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
          <Pagination
            count={pagination.last_page}
            page={pagination.current_page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Contact Details Modal for Trash */}
      <ContactModal open={modalOpen} onClose={handleCloseModal} contact={selectedContact} />

      {/* Permanent Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="permanent-delete-dialog-title"
        aria-describedby="permanent-delete-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 1
          }
        }}
      >
        <DialogTitle id="permanent-delete-dialog-title" sx={{ textAlign: 'center', py: 3 }}>
          <WarningAmber
            sx={{
              fontSize: 60,
              color: palette.warning.main,
              mb: 2
            }}
          />
          <Typography
            variant="h6"
            sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
          >
            Permanent Deletion
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <DialogContentText
            id="permanent-delete-dialog-description"
            sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}
          >
            Are you sure you want to permanently delete contact from{' '}
            <strong>{contactToDelete?.name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              borderColor: palette.grey[400],
              color: palette.text.primary,
              '&:hover': {
                borderColor: palette.grey[600],
                backgroundColor: palette.action.hover
              },
              minWidth: 100
            }}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              backgroundColor: palette.error.main,
              color: palette.error.contrastText,
              '&:hover': {
                backgroundColor: palette.error.dark
              },
              minWidth: 100
            }}
            disabled={deleteLoading}
            startIcon={<Delete />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContactTrash;